"use client";

import { misc, util } from "@/common/theme";
import CustomSplitText from "@/mono/components/CustomSplitText";
import useDelayedLoaderAnimation from "@/mono/lib/hooks/useDelayedLoaderAnimation";
import { mediaMobile } from "@/mono/lib/responsive";
import { EASE_CUSTOM_3 } from "@/mono/lib/styles/easing";
import { setTypography, setVh } from "@/mono/lib/styles/mixins";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import styled, { css } from "styled-components";

import BorderedIcon from "@/components/BorderedIcon";
import { convertMultiParagraphToNineFormat } from "../contentSections/MultiLineTextComponent";
import useLoop from "@/mono/lib/hooks/useLoop";
import useResize from "@/mono/lib/hooks/useResize";
import { map } from "@/mono/lib/math";
import useEvent from "mono/lib/hooks/useEvent";
import SplitText from "mono/components/SplitText";

const SoftBigHeroTitle = forwardRef(({ children }, ref) => {
  const title = useRef();

  const inAnimation = () => ({
    opacity: 1,
    ease: EASE_CUSTOM_3,
    duration: 1,
    stagger: {
      amount: 1,
      from: "random",
    },
  });

  useDelayedLoaderAnimation(() => {
    title.current?.animateIn();
  }, 400);
  useEvent("SERVICE:ON_ENTERING", ({ from }) => {
    if (title.current && from) title.current?.animateIn?.();
  });
  useEvent("WHAT_WE_DO:ON_ENTERING", ({ from }) => {
    if (title.current && from) title.current?.animateIn?.();
  });

  useImperativeHandle(ref, () => ({
    element: title.current.element,
  }));

  return (
    <CustomSplitText
      animation={{
        in: inAnimation,
        set: {
          opacity: 0,
        },
      }}
      shouldRevert={false}
      tag="h1"
      type="chars"
      ref={title}
      animateOnScroll={false}
    >
      {children}
    </CustomSplitText>
  );
});

export default function ServicePageHero({
  title = "Web-design",
  header,
  paragraph,
  cta,
}) {
  const headerRef = useRef();
  const paragraphElements = useRef([]);
  const ctaRef = useRef();

  const bounds = useRef();
  const gap = useRef(0);
  const singleItemBounds = useRef();

  const titleWrapper = useRef();
  const titleWrapperInner = useRef();
  const [title1, title2, title3] = [useRef(), useRef(), useRef()];

  const formattedParagraph = convertMultiParagraphToNineFormat(paragraph);

  useResize(
    (size) => {
      if (!title1.current || singleItemBounds.current) return;
      bounds.current = titleWrapperInner.current.getBoundingClientRect();
      singleItemBounds.current = title1.current.element.getBoundingClientRect();
      titleWrapper.current.style.transform = `translate(${
        bounds.current.width / -4
      }px, calc(-50% - ${misc.navigationHeight()}))`;

      const title1Bounds = title1.current.element.getBoundingClientRect();
      const title2Bounds = title2.current.element.getBoundingClientRect();
      gap.current = title2Bounds.left - title1Bounds.right;
    },
    { immediate: true },
    [titleWrapper.current, title1.current]
  );

  const { start, stop } = useLoop(
    ({ progress }) => {
      const totalWidth = singleItemBounds.current.width + gap.current;
      const x = progress * totalWidth * -1;

      if (titleWrapperInner?.current) {
        titleWrapperInner.current.style.transform = `translateX(${x}px)`;
      }
    },
    {
      speed: 0.05,
    }
  );

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  function animateIn() {
    start();
    headerRef.current?.animateIn();
    paragraphElements.current.forEach((p, i) =>
      p?.animateIn({ delay: i * 0.2 + 1 })
    );
    ctaRef.current?.animateIn?.({ delay: 1.4 });
  }

  useDelayedLoaderAnimation(() => {
    animateIn();
  }, 600);

  useEvent("SERVICE:ON_ENTERING", ({ from }) => {
    if (from) animateIn();
  });
  useEvent("WHAT_WE_DO:ON_ENTERING", ({ from }) => {
    if (from) animateIn();
  });

  return (
    <Wrapper>
      <div className="title-wrapper" ref={titleWrapper}>
        <div className="title-wrapper-inner" ref={titleWrapperInner}>
          <SoftBigHeroTitle ref={title1}>{title}</SoftBigHeroTitle>
          <SoftBigHeroTitle ref={title2}>{title}</SoftBigHeroTitle>
          <SoftBigHeroTitle ref={title3}>{title}</SoftBigHeroTitle>
        </div>
      </div>
      <div className="header-wrapper">
        <SplitText tag="h3" ref={headerRef} animateOnScroll={false}>
          {header}
        </SplitText>
      </div>

      {formattedParagraph && (
        <div className="paragraph-wrapper">
          {formattedParagraph.map((p, i) => (
            <SplitText
              key={i}
              tag={p.tag}
              ref={(el) => (paragraphElements.current[i] = el)}
              animateOnScroll={false}
            >
              {p.text}
            </SplitText>
          ))}
          {cta && (
            <BorderedIcon
              animateOnScroll={false}
              text={cta.text}
              href={cta.url}
              ref={ctaRef}
              dontTriggerPageTransition={true}
            />
          )}
        </div>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${util.inner}
  height: ${setVh(100)};
  position: relative;

  position: relative;
  .title-wrapper {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    overflow: visible;
  }

  .title-wrapper-inner {
    width: 100%;
    height: 100%;
    display: flex;
    overflow: visible;
    h1 {
      margin-right: 150rem;
    }
  }

  h1 {
    ${setTypography("serviceHeroTitle")}
    white-space: nowrap;
    color: ${({ theme }) => theme.theme.backgroundAccentLight};
  }

  .header-wrapper {
    position: absolute;
    top: calc(40% - ${misc.navigationHeight()});
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    gap: 150rem;
  }

  h3 {
    max-width: unset;
    ${setTypography("serviceHeroSubTitle")}
  }

  .paragraph-wrapper {
    position: absolute;
    left: 50%;
    bottom: 15%;
    max-width: 1200rem;

    display: flex;
    flex-direction: column;
    gap: 50rem;
    align-items: flex-start;
  }

  ${mediaMobile(css`
    h3 {
      max-width: 100%;
    }
    .title-wrapper {
      left: 0;
    }

    .header-wrapper,
    .paragraph-wrapper {
      width: calc(100% - ${misc.basePadding()} * 2);
      /* position: relative; */
    }

    .paragraph-wrapper {
      left: 50%;
      transform: translateX(-50%);
    }
  `)}
`;
